import { ConversationStatus, ConversationSummaryDTO } from 'models/conversations'

import { PageableResponse } from 'models/misc'
import { resolveExpectedParticipantRole } from './resolveExpectedRole'
import { useAuth0 } from 'utils/auth'
import { useGetConversations } from 'dataQueries/conversations.query'
import { useMemo } from 'react'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Resolves the caller's platform UUID by scanning any cached conversation for the participant
 * entry that matches the caller's platform role. The participant id is the canonical id stamped on
 * `senderId` for messages — preferring it makes the realtime patchers correct even when
 * `baseUserData.internalId` is empty or out-of-sync. Falls back to `internalId` if no cached
 * conversation has surfaced yet.
 */
export const useResolvedCurrentUserId = (): string | undefined => {
  const { roles } = useAuth0()
  const { baseUserData } = useUserData()
  const { data: pageAny } = useGetConversations()
  const { data: pageActive } = useGetConversations(ConversationStatus.ACTIVE)

  return useMemo(() => {
    const expectedRole = resolveExpectedParticipantRole(baseUserData?.platformRole, roles)
    if (!expectedRole) return baseUserData?.internalId

    const scan = (page: PageableResponse<ConversationSummaryDTO[]> | undefined) => {
      for (const conversation of page?.content ?? []) {
        const hit = conversation.participants.find((p) => p.role === expectedRole)?.userId
        if (hit) return hit
      }
      return undefined
    }

    return scan(pageAny) ?? scan(pageActive) ?? baseUserData?.internalId
  }, [baseUserData?.internalId, baseUserData?.platformRole, pageAny, pageActive, roles])
}
